import {
  Button,
  CloseButton,
  Flex,
  Grid,
  Heading,
  Modal,
  Pill,
  Spinner,
  Text,
  TextInput,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { TermsContext } from "../../../utils/Context";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { InputDate } from "../../InputDate";

const TermModal = ({ open, setOpen, term, handleSave }) => {
  const { t } = useTranslation();
  const { insightsTerms, canvasTerms, academicYears } =
    useContext(TermsContext);
  const showNotification = useNotification();
  const [loading, setLoading] = useState(false);
  const [termName, setTermName] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [includedCanvasTerms, setIncludedCanvasTerms] = useState([]);

  const [inExistingYearDialog, setInExistingYearDialog] = useState(false);
  const [existingYear, setExistingYear] = useState({});

  useEffect(() => {
    if (term) {
      setTermName(term.name);
      setStartDate(new Date(term.start_at));
      setEndDate(new Date(term.end_at));
    }
  }, [term]);

  useEffect(() => {
    let selectedTerms = canvasTerms.filter(
      (term) =>
        new Date(term.start_at).setHours(0, 0, 0, 0) >=
          startDate.setHours(0, 0, 0, 0) &&
        new Date(term.start_at).setHours(0, 0, 0, 0) <=
          endDate.setHours(0, 0, 0, 0)
    );

    // Remove default future term if Canvas terms are selected
    if (
      selectedTerms.length > 1 &&
      selectedTerms.some((term) => term.canvas_id === -1)
    ) {
      selectedTerms = selectedTerms.filter((term) => term.canvas_id !== -1);
    }

    setIncludedCanvasTerms(
      selectedTerms
        // Sorting Canvas terms by start date
        .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
    );
  }, [startDate, endDate, canvasTerms]);

  const checkOverlap = () => {
    let overlap = "";
    const selectedTermStart = new Date(startDate).setHours(0, 0, 0, 0);
    const selectedTermEnd = new Date(endDate).setHours(0, 0, 0, 0);
    insightsTerms.forEach((t) => {
      if (term?.id !== t.id) {
        const termStart = new Date(t.start_at).setHours(0, 0, 0, 0);
        const termEnd = new Date(t.end_at).setHours(0, 0, 0, 0);
        if (
          (selectedTermStart >= termStart && selectedTermStart <= termEnd) ||
          (selectedTermEnd >= termStart && selectedTermEnd <= termEnd) ||
          (selectedTermStart <= termStart && selectedTermEnd >= termEnd)
        ) {
          overlap = t;
        }
      }
    });
    return overlap;
  };

  const checkInAcademicYear = async () => {
    const selectedTermStart = new Date(startDate).setHours(0, 0, 0, 0);
    const selectedTermEnd = new Date(endDate).setHours(0, 0, 0, 0);

    let inExistingYear = false;
    academicYears.forEach((year) => {
      const yearStart = new Date(year.start_at).setHours(0, 0, 0, 0);
      const yearEnd = new Date(year.end_at).setHours(0, 0, 0, 0);
      if (selectedTermStart >= yearStart && selectedTermEnd <= yearEnd) {
        inExistingYear = true;
        setExistingYear(year);
      }
    });
    if (inExistingYear) {
      setInExistingYearDialog(true);
    } else {
      setLoading(true);
      await handleSave(
        termName,
        startDate,
        endDate,
        includedCanvasTerms.map((t) => t.id)
      );
      setLoading(false);
    }
  };

  const handleSaveTerm = async () => {
    const overlapTerm = checkOverlap();
    if (overlapTerm) {
      showNotification(
        t("insightsTerms.overlapError", { term: overlapTerm.name }),
        ERROR
      );
    } else if (startDate > endDate) {
      showNotification(t("insightsTerms.dateError"), ERROR);
    } else {
      if (term?.id) {
        setLoading(true);
        await handleSave(
          termName,
          startDate,
          endDate,
          includedCanvasTerms.map((t) => t.id)
        );
        setLoading(false);
      } else {
        checkInAcademicYear();
      }
    }
  };

  const handleSaveTermAndYear = async () => {
    setInExistingYearDialog(false);
    setLoading(true);
    await handleSave(
      termName,
      startDate,
      endDate,
      includedCanvasTerms.map((t) => t.id),
      existingYear
    );
    setLoading(false);
  };

  const handleClose = () => {
    setTermName(term?.name || "");
    setStartDate(term?.start_at ? new Date(term.start_at) : new Date());
    setEndDate(term?.end_at ? new Date(term.end_at) : new Date());
    setLoading(false);
  };

  return (
    <>
      <Modal
        open={open}
        size="medium"
        label={term ? t("insightsTerms.editTerm") : t("insightsTerms.addTerm")}
        onExited={handleClose}
      >
        <Modal.Header>
          <Flex justifyItems="space-between">
            <Flex.Item>
              <Heading level="h2" as="h1">
                {term
                  ? t("insightsTerms.editTerm")
                  : t("insightsTerms.addTerm")}
              </Heading>
            </Flex.Item>
            <Flex.Item>
              <CloseButton
                size="small"
                screenReaderLabel={t("global.close")}
                onClick={() => setOpen(false)}
              />
            </Flex.Item>
          </Flex>
        </Modal.Header>
        <Modal.Body>
          <Flex wrap="wrap" gap="small">
            <Flex.Item size="100%">
              <TextInput
                renderLabel={t("assessment.name")}
                value={termName}
                onChange={(e) => setTermName(e.target.value)}
              />
            </Flex.Item>
            <Flex.Item>
              <InputDate
                id="date-1-selector"
                label={t("cyclesAndGoals.startDate")}
                selectHandler={(date) => setStartDate(date)}
                selectedDate={startDate}
              />
            </Flex.Item>
            <Flex.Item>
              <InputDate
                id="date-2-selector"
                label={t("cyclesAndGoals.endDate")}
                selectHandler={(date) => setEndDate(date)}
                selectedDate={endDate}
              />
            </Flex.Item>
            <Flex.Item size="100%">
              <Text lineHeight="double">
                <Text weight="bold">{t("insightsTerms.includedTerms")}: </Text>
                {includedCanvasTerms.length > 0
                  ? includedCanvasTerms.map((term, key) => (
                      <Pill key={key} color="info" margin="0 small 0 0">
                        {term.name}
                      </Pill>
                    ))
                  : t("global.none")}
              </Text>
            </Flex.Item>
          </Flex>
        </Modal.Body>
        <Modal.Footer>
          <Flex wrap="wrap" gap="small">
            <Flex.Item>
              <Button onClick={() => setOpen(false)}>
                {t("global.cancel")}
              </Button>
            </Flex.Item>
            <Flex.Item>
              <Button
                type="submit"
                color="primary"
                onClick={handleSaveTerm}
                interaction={!!termName && !loading ? "enabled" : "disabled"}
              >
                {loading ? (
                  <Spinner
                    size="x-small"
                    renderTitle={t("global.pleaseWait")}
                  />
                ) : (
                  t("global.save")
                )}
              </Button>
            </Flex.Item>
          </Flex>
        </Modal.Footer>
      </Modal>
      {/* Dialog to let user know the newly created insights term will be added to Academic Year, if it can */}
      <Modal
        open={inExistingYearDialog}
        onDismiss={() => setInExistingYearDialog(false)}
        size="large"
        label={t("global.note")}
      >
        <Modal.Header>
          <Grid hAlign="space-between" vAlign="middle">
            <Grid.Row>
              <Grid.Col>
                <Heading level="h2" as="h2">
                  {t("global.note")}
                </Heading>
              </Grid.Col>
              <Grid.Col width="auto">
                <CloseButton
                  size="small"
                  screenReaderLabel={t("global.close")}
                  onClick={() => setInExistingYearDialog(false)}
                />
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </Modal.Header>
        <Modal.Body>
          {t("insightsTerms.overlappingYear", {
            termName: termName,
            yearName: existingYear.name,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button
            margin="0 small 0 0"
            onClick={() => setInExistingYearDialog(false)}
          >
            {t("global.cancel")}
          </Button>
          <Button color="primary" onClick={handleSaveTermAndYear}>
            {t("global.continue")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TermModal;
